import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Typography, SvgIcon, Grid, useMediaQuery } from '@material-ui/core';

// CONSTANTS
import { ROUTE_SUBSCRIPTION, ROUTE_WP_PAGE, ROUTE_DISCOVER_APP } from '@/constants/routes';
import {
  LOCATIONS_SLING_BANNER,
  LOCATIONS_SLING_PROMO_BANNER,
  LOCATIONS_SLING_PROMO_PEACOCK_BANNER,
  LOCATIONS_HULU_LIVE_BANNER,
} from '@/constants/locations';

// UTILITY
import { getLink } from '@/utility/routes';
import { getS3ImageUrl } from '@/utility/content';

// COMPONENTS
import RouterLink from '@/components/Link';

// ASSETS
import { ReactComponent as CheckSvg } from '@/assets/images/check.svg';
import { ReactComponent as PromoSvg } from '@/assets/images/promo-label-for-bunner.svg';

// STYLES
import { Container, SpecialPrice } from './styles';

const channelESPNImg = getS3ImageUrl('/logos/channels/color/espn_color.png');
const channelCNNImg = getS3ImageUrl('/logos/channels/color/cnn_usa_color.png');
const channelTBSImg = getS3ImageUrl('/logos/channels/color/tbs_new_color.png');
const channelUSAImg = getS3ImageUrl('/logos/channels/color/usa_new_color.png');
const channelHistoryImg = getS3ImageUrl('/logos/channels/color/history_color.png');
const channelDiscoveryImg = getS3ImageUrl('/logos/channels/color/discovery_new_color.png');
const channelAEImg = getS3ImageUrl('/logos/channels/color/ae_new_color.png');
const channelFXImg = getS3ImageUrl('/logos/channels/color/fx_color.png');
const channelABCImg = getS3ImageUrl('/logos/channels/color/abc_new_color.png');
const channelCBSImg = getS3ImageUrl('/logos/channels/color/cbs_new_color.png');
const channelNBCImg = getS3ImageUrl('/logos/channels/color/nbc_new_color.png');
const channelFOXImg = getS3ImageUrl('/logos/channels/color/fox_new_color.png');
const channelHGTVImg = getS3ImageUrl('/logos/channels/color/hgtv_new_color.png');
const channelTNTImg = getS3ImageUrl('/logos/channels/color/tnt_new_color.png');

const sling_channels = [
  { name: 'espn', src: channelESPNImg },
  { name: 'cnn', src: channelCNNImg },
  { name: 'tbs', src: channelTBSImg },
  { name: 'usa', src: channelUSAImg },
  { name: 'history', src: channelHistoryImg },
  { name: 'discovery', src: channelDiscoveryImg },
  { name: 'ae', src: channelAEImg },
  { name: 'fx', src: channelFXImg },
];

const hulu_channels = [
  { name: 'abc', src: channelABCImg },
  { name: 'cbs', src: channelCBSImg },
  { name: 'nbc', src: channelNBCImg },
  { name: 'fox', src: channelFOXImg },
  { name: 'espn', src: channelESPNImg },
  { name: 'history', src: channelHistoryImg },
  { name: 'hgtv', src: channelHGTVImg },
  { name: 'tnt', src: channelTNTImg },
];

const BannerWithChannels = ({ type }) => {
  const [t] = useTranslation();
  const { isIframe } = useSelector(({ app }) => ({
    isIframe: app.isIframe,
  }));
  const upSm = useMediaQuery(({ breakpoints }) => breakpoints.up('sm'));
  const upMd = useMediaQuery(({ breakpoints }) => breakpoints.up('md'));

  const copies = {
    title: t('swimlanes.sling.title'),
    subtitle: t('swimlanes.sling.subtitle'),
    promoTitle: t('swimlanes.sling.promo.title'),
    benefit1: t('swimlanes.sling.benefits.item1'),
    benefit2: t('swimlanes.sling.benefits.item2'),
    benefit3: t('swimlanes.sling.benefits.item3'),
    channelsTitle: t('swimlanes.sling.channels.title'),
  };
  let price = '$20';
  let oldPrice = '$40/month';
  let location = LOCATIONS_SLING_BANNER;
  let appLink = getLink(ROUTE_SUBSCRIPTION, { slug: 'sling-tv' });
  let logo = getS3ImageUrl('/logos/apps/transparent/padded/slingtv.png');
  let channels = sling_channels;

  if (type === 'sling-promo') {
    copies.title = t('swimlanes.sling-promo.title');
    copies.subtitle = t('swimlanes.sling-promo.subtitle');
    copies.promoTitle = t('swimlanes.sling-promo.promo.title');
    location = LOCATIONS_SLING_PROMO_BANNER;
    appLink = getLink(ROUTE_WP_PAGE, { slug: 'hulu-terms' });
    logo = getS3ImageUrl('/sling-hulu-logo.png', '/cp-uploads');
    channels = sling_channels;
  } else if (type === 'sling-promo-peacock') {
    copies.title = t('swimlanes.sling-promo-peacock.title');
    copies.subtitle = t('swimlanes.sling-promo-peacock.subtitle');
    copies.promoTitle = t('swimlanes.sling-promo-peacock.promo.title');
    location = LOCATIONS_SLING_PROMO_PEACOCK_BANNER;
    appLink = getLink(ROUTE_WP_PAGE, { slug: 'sling-peacock-terms' });
    logo = getS3ImageUrl('/sling-peacock-2.png', '/cp-uploads');
    channels = sling_channels;
  } else if (type === 'hulu-live') {
    copies.title = t('swimlanes.hulu-live.title');
    copies.subtitle = t('swimlanes.hulu-live.subtitle');
    copies.promoTitle = t('swimlanes.hulu-live.promo.title');
    copies.benefit1 = t('swimlanes.hulu-live.benefits.item1');
    copies.benefit2 = t('swimlanes.hulu-live.benefits.item2');
    copies.benefit3 = t('swimlanes.hulu-live.benefits.item3');
    copies.channelsTitle = t('swimlanes.hulu-live.channels.title');
    location = LOCATIONS_HULU_LIVE_BANNER;
    appLink = getLink(ROUTE_DISCOVER_APP, { slug: 'hulu-live' });
    logo = getS3ImageUrl('/hulu-live-new-app.png', '/cp-uploads');
    channels = hulu_channels;
    price = '$69.99';
    oldPrice = '';
  }

  const Symbol = () => <SvgIcon viewBox="0 0 33 34" component={CheckSvg} />;
  const Promo = () =>
    copies.promoTitle && (
      <SpecialPrice className="special-price">
        <div className="text-wrap">
          <p>{copies.promoTitle}</p>
        </div>
        <SvgIcon viewBox="0 0 94 98" fontSize="inherit" component={PromoSvg} />
      </SpecialPrice>
    );

  const benefits = [
    { title: copies.benefit1 },
    { title: copies.benefit2 },
    { title: copies.benefit3 },
  ];

  const list = (
    <Grid container spacing={upSm ? 2 : 1} className="list" direction={upSm ? 'row' : 'column'}>
      {benefits.map((item, i) => (
        <Grid item key={i}>
          <div className="item">
            <Symbol />
            <span>{item.title}</span>
          </div>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container className="banner-app section section--green">
      <RouterLink
        className="banner-app__link"
        to={appLink}
        target={isIframe ? '_blank' : '_self'}
        location={location}
        withoutIframe
      />
      <Promo />
      <Grid container spacing={upSm ? 3 : 1} direction={upSm ? 'row' : 'column'} wrap="nowrap">
        <Grid className="shrink-0" item>
          <div className="banner-app__logo">
            <img className="banner-app__logo-img" alt="logo" src={logo} />
          </div>
        </Grid>
        <Grid item>
          <div className="banner-app__desc">
            <Typography variant="h3">{copies.title}</Typography>
            <Typography variant="h5">{copies.subtitle} </Typography>
            <Typography className="old-price">
              <strike>{oldPrice}</strike>
            </Typography>
            <Typography className="new-price" variant="h5">
              {price}
            </Typography>
          </div>
          {upMd && list}
        </Grid>
      </Grid>
      {!upMd && list}
      <div className="banner-app__channels">
        <Typography className={clsx(!upSm && 'p3')} variant={upSm ? 'h5' : 'body1'}>
          {copies.channelsTitle}
        </Typography>
        <div className="channels">
          <Grid container spacing={upSm ? 3 : 1}>
            {channels.map(({ src, name }, i) => (
              <Grid item key={i}>
                <div className="channels__item">
                  <img className="channels__icon" src={src} alt={name} />
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    </Container>
  );
};

BannerWithChannels.defaultProps = {
  type: 'sling',
};

BannerWithChannels.propTypes = {
  type: PropTypes.string,
};

export default React.memo(BannerWithChannels);
